import {
  Container,
  Header,
  ColumnLayout,
  Box,
  BoxProps,
} from '@cloudscape-design/components';
import { ScoreFilterType, QuestionResultType } from 'src/contstant/commonLabels';
import {
  Audit,
  GetAuditSummaryResponse,
  QuestionWithResponse,
} from 'src/types/helios/Audit';
import { useMemo } from 'react';

interface ScoreBreakdownProps {
  auditSummary?: GetAuditSummaryResponse;
  selectedFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  allQuestions?: QuestionWithResponse[]; // All loaded questions for accurate counts
}

const ScoreBreakdown = ({
  auditSummary,
  selectedFilter,
  onFilterChange,
  allQuestions,
}: ScoreBreakdownProps) => {
  // Calculate accurate counts from all loaded questions
  const questionCounts = useMemo(() => {
    // Prefer allQuestions from the hook if available, otherwise use questions from auditSummary
    const questions =
      allQuestions && allQuestions.length > 0
        ? allQuestions
        : auditSummary?.questions || [];

    if (questions.length === 0) {
      // Fallback to audit metrics if no questions available
      const metrics = auditSummary?.audit?.metrics || {
        passedQuestions: 0,
        failedQuestions: 0,
        naQuestions: 0,
        totalQuestions: 0,
      };

      return {
        passed: metrics.passedQuestions || 0,
        failed: metrics.failedQuestions || 0,
        na: metrics.naQuestions || 0,
        total: metrics.totalQuestions || 0,
      };
    }

    // Calculate from loaded questions
    const passed = questions.filter(
      (q) =>
        q.responseDetails?.mainQuestionResponse?.responseResult?.toLowerCase() ===
        QuestionResultType.PASS.toLowerCase(),
    ).length;

    const failed = questions.filter(
      (q) =>
        q.responseDetails?.mainQuestionResponse?.responseResult?.toLowerCase() ===
        QuestionResultType.FAIL.toLowerCase(),
    ).length;

    const na = questions.filter(
      (q) =>
        q.responseDetails?.mainQuestionResponse?.responseResult?.toLowerCase() ===
        QuestionResultType.NA.toLowerCase(),
    ).length;

    return {
      passed,
      failed,
      na,
      total: questions.length,
    };
  }, [allQuestions, auditSummary]);
  const ScoreBox = ({
    number,
    label,
    color,
    filterKey = ScoreFilterType.PASS,
  }: {
    number: number;
    label: string;
    color: BoxProps.Color;
    filterKey?: string;
  }) => (
    <div
      data-testid={`score-box-${filterKey.toLowerCase()}`}
      onClick={() => onFilterChange(selectedFilter === filterKey ? null : filterKey)}
      style={{
        cursor: 'pointer',
        backgroundColor: selectedFilter === filterKey ? '#e6f3ff' : 'transparent',
        borderRadius: '4px',
        padding: '8px',
      }}
    >
      <Box textAlign="center">
        <Box color={color} fontSize="heading-xl" fontWeight="bold">
          {number}
        </Box>
        <Box color="text-body-secondary">{label}</Box>
      </Box>
    </div>
  );

  if (!auditSummary?.audit?.metrics) {
    return <></>;
  }

  return (
    <Container
      data-testid="score-breakdown-container"
      header={<Header variant="h3">Audit Response Breakdown</Header>}
    >
      <ColumnLayout data-testid="score-breakdown-layout" columns={4} variant="text-grid">
        <ScoreBox
          number={questionCounts.passed}
          label="Pass"
          color="text-status-success"
          filterKey={ScoreFilterType.PASS}
        />
        <ScoreBox
          number={questionCounts.failed}
          label="Fail"
          color="text-status-error"
          filterKey={ScoreFilterType.FAIL}
        />
        <ScoreBox
          number={questionCounts.na}
          label="N/A"
          color="text-status-info"
          filterKey={ScoreFilterType.NA}
        />
        <ScoreBox
          number={questionCounts.total}
          label="Total Questions"
          color="text-label"
          filterKey={ScoreFilterType.TOTAL}
        />
      </ColumnLayout>
    </Container>
  );
};

export default ScoreBreakdown;
